import { Card } from "shared/components/Card";
import { Switch } from "shared/components/forms/Switch";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { ApartmentsComVariant, Unit } from "types/landlord/address";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import addressApi from "api/landlord/address";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { useSettingsController } from "views/settings-integration/controller";
import { PropertyKind } from "shared/types";
import { Show } from "shared/components";
import { useAuthContext } from "providers/Auth";
import { IntegrationId } from "views/settings-integration/components/AddIntegrationModal/types";
import { BoomBrandMark } from "shared/components/SyndicationChannels/BoomBrandMark";
import { ComingSoonBadge } from "shared/components/SyndicationChannels/ComingSoonBadge";
import { SourceCheckbox } from "shared/components/SyndicationChannels/SourceCheckbox";
import { SourceLogo } from "shared/components/SyndicationChannels/SourceLogo";
import { TreeRow } from "shared/components/SyndicationChannels/TreeRow";
import {
  SyndicationState,
  apartmentsVariantForKind,
  diffMagicLinkPayload,
  readSyndicationState,
  withApartmentsComToggled,
  withApartmentsVariant,
  withDwellsyToggled,
  withZillowToggled,
  withZumperToggled
} from "shared/components/SyndicationChannels/syndication";
import { PmsSyncOption } from "shared/app/RentalInfoForm/components/PmsSyncOption";
import { PMSValue } from "shared/app/RentalInfoForm/components/PmsValue";
import { IconAILeasingAgent } from "shared/components/ai/IconAI/icons/IconAILeasingAgent";
import { FEATURES } from "types/landlord/partner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import {
  ALLOW_APPLICATIONS_LOCKED_BY_LISTED,
  ALLOW_APPLICATIONS_PMS_SYNCED,
  LISTED_PMS_SYNCED,
  LISTED_REQUIRES_APPLICATIONS
} from "shared/app/RentalInfoForm/constants";

interface ListingSectionProps {
  propertyId: number;
  propertyKind: PropertyKind | null;
  unit: Unit;
}

const CLEARED_SYNDICATION: SyndicationState = {
  zillow: false,
  zillowSpotlight: false,
  apartmentsCom: false,
  apartmentsComVariant: null,
  zumper: false,
  dwellsy: false
};

type TopLevelSource =
  | "listings"
  | "aiLeasingAgent"
  | "zillow"
  | "apartments"
  | "zumper"
  | "dwellsy";

const computeLastTopLevel = ({
  aiLeasingAgentVisible,
  zillowEnabled,
  apartmentsComEnabled,
  zumperEnabled,
  dwellsyEnabled
}: {
  aiLeasingAgentVisible: boolean;
  zillowEnabled: boolean;
  apartmentsComEnabled: boolean;
  zumperEnabled: boolean;
  dwellsyEnabled: boolean;
}): TopLevelSource => {
  if (dwellsyEnabled) return "dwellsy";
  if (zumperEnabled) return "zumper";
  if (apartmentsComEnabled) return "apartments";
  if (zillowEnabled) return "zillow";
  if (aiLeasingAgentVisible) return "aiLeasingAgent";
  return "listings";
};

const booleanLabel = (value?: boolean) => {
  if (value == null) return undefined;
  return value ? "On" : "Off";
};

const RowConnector = () => (
  <span aria-hidden className="flex items-center shrink-0">
    <span className="w-8 border-t border-content-100" />
    <span className="size-1.5 rounded-full bg-content-100" />
  </span>
);

const syndicationEqual = (a: SyndicationState, b: SyndicationState) =>
  a.zillow === b.zillow &&
  a.zillowSpotlight === b.zillowSpotlight &&
  a.apartmentsCom === b.apartmentsCom &&
  a.apartmentsComVariant === b.apartmentsComVariant &&
  a.zumper === b.zumper &&
  a.dwellsy === b.dwellsy;

const REPLICA_RETRY_DELAY_MS = 200;
const REPLICA_MAX_RETRIES = 3;
const AUTOSAVE_DEBOUNCE_MS = 500;

export const ListingSection = ({
  propertyId,
  propertyKind,
  unit
}: ListingSectionProps) => {
  const dictionary = useUnitsPropertiesDictionary();
  const queryClient = useQueryClient();
  const { isEverySyncEnabled } = useSettingsController();
  const { partner, type: partnerType } = useAuthContext();

  const defaultApartmentsComVariant = apartmentsVariantForKind(propertyKind);

  const aiLeasingAgentVisible = Boolean(
    partner?.features.includes(FEATURES.CRM_AI_VOICE_AGENT)
  );
  // Opt-in flag (DB default false): only an explicit true means the AI leasing
  // agent can see this unit, so a missing/null value renders unchecked.
  const aiLeasingAgentEnabled = unit.ai_leasing_agent_enabled === true;

  const [linkIsActive, setLinkIsActive] = useState<boolean>(
    Boolean(unit.magic_link?.active)
  );
  const [unitListed, setUnitListed] = useState<boolean>(Boolean(unit.listed));
  const [syndication, setSyndication] = useState<SyndicationState>(() =>
    readSyndicationState(unit, defaultApartmentsComVariant)
  );

  const pmsManaged = isEverySyncEnabled && Boolean(unit.pms_id);
  const [overrideEdits, setOverrideEdits] = useState<{
    listed?: boolean;
    available_for_application?: boolean;
  }>({});
  const listedSynced =
    overrideEdits.listed ?? unit.pms_overrides?.listed ?? true;
  const allowApplyingSynced =
    overrideEdits.available_for_application ??
    unit.pms_overrides?.available_for_application ??
    true;

  const committedRef = useRef({
    linkIsActive,
    unitListed,
    syndication
  });
  const awaitingRefetchRef = useRef(false);
  const replicaRetryCountRef = useRef(0);
  const replicaRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const invalidatePropertyDetail = useCallback(() => {
    propertyKind === PropertyKind.SingleFamily
      ? queryClient.invalidateQueries({
          queryKey: ["properties", String(propertyId)]
        })
      : queryClient.invalidateQueries({
          queryKey: ["unit", String(propertyId), String(unit.id)]
        });
  }, [propertyKind, propertyId, unit.id, queryClient]);

  const handleAwaitingRefetch = useCallback(
    (matched: boolean): "done" | "continue" => {
      if (matched) {
        awaitingRefetchRef.current = false;
        replicaRetryCountRef.current = 0;
        if (replicaRetryTimerRef.current) {
          clearTimeout(replicaRetryTimerRef.current);
          replicaRetryTimerRef.current = null;
        }
        return "done";
      }

      if (replicaRetryCountRef.current < REPLICA_MAX_RETRIES) {
        replicaRetryCountRef.current += 1;
        if (replicaRetryTimerRef.current)
          clearTimeout(replicaRetryTimerRef.current);
        replicaRetryTimerRef.current = setTimeout(
          invalidatePropertyDetail,
          REPLICA_RETRY_DELAY_MS * replicaRetryCountRef.current
        );
        return "done";
      }

      awaitingRefetchRef.current = false;
      replicaRetryCountRef.current = 0;
      return "continue";
    },
    [invalidatePropertyDetail]
  );

  useEffect(
    () => () => {
      if (replicaRetryTimerRef.current)
        clearTimeout(replicaRetryTimerRef.current);
    },
    []
  );

  const enableAllowApplying = () => {
    setLinkIsActive(true);
  };

  const disableAllowApplying = () => {
    if (unitListed) {
      setLinkIsActive(false);
      setUnitListed(false);
      setSyndication(CLEARED_SYNDICATION);
    } else setLinkIsActive(false);
  };

  const enableShowOnPortfolio = () => {
    if (!linkIsActive) {
      setUnitListed(true);
      setLinkIsActive(true);
    } else setUnitListed(true);
  };

  const allowApplyingToggleLocked = linkIsActive && unitListed && listedSynced;
  const listedToggleLocked =
    !unitListed && !linkIsActive && allowApplyingSynced;

  let allowApplyingTooltip: string | null = null;
  if (allowApplyingSynced) allowApplyingTooltip = ALLOW_APPLICATIONS_PMS_SYNCED;
  else if (allowApplyingToggleLocked)
    allowApplyingTooltip = ALLOW_APPLICATIONS_LOCKED_BY_LISTED;

  let listedTooltip: string | null = null;
  if (listedSynced) listedTooltip = LISTED_PMS_SYNCED;
  else if (listedToggleLocked) listedTooltip = LISTED_REQUIRES_APPLICATIONS;

  const disableShowOnPortfolio = () => {
    setUnitListed(false);
    setSyndication(CLEARED_SYNDICATION);
  };

  const { mutate: updateUnitSettings, isPending } = useMutation({
    mutationKey: ["MAGIC_LINK_UPDATE", unit.magic_link?.id],
    mutationFn: async () => {
      const snapshot = {
        linkIsActive,
        unitListed,
        syndication
      };

      if (!unit.magic_link?.id) {
        committedRef.current = snapshot;
        return;
      }

      const payload = diffMagicLinkPayload(committedRef.current, snapshot);
      if (Object.keys(payload).length === 0) {
        committedRef.current = snapshot;
        return;
      }

      await addressApi.updateMagicLink(unit.magic_link.id, payload);

      committedRef.current = snapshot;
    },
    onSuccess: () => {
      awaitingRefetchRef.current = true;
      replicaRetryCountRef.current = 0;
      if (replicaRetryTimerRef.current) {
        clearTimeout(replicaRetryTimerRef.current);
        replicaRetryTimerRef.current = null;
      }
      invalidatePropertyDetail();
    },
    onError: (error) => {
      const last = committedRef.current;
      setLinkIsActive(last.linkIsActive);
      setUnitListed(last.unitListed);
      setSyndication(last.syndication);
      toast.error(`Failed to update settings: ${getErrorMessage(error)}`);
    }
  });

  const pmsAvailability = unit.pms_values?.available_for_application;
  const pmsAllowApplyingValue =
    pmsAvailability ??
    (allowApplyingSynced ? Boolean(unit.magic_link?.active) : undefined);
  const pmsListedValue =
    pmsAvailability ?? (listedSynced ? Boolean(unit.listed) : undefined);

  const { mutate: updatePmsOverrides } = useMutation({
    mutationKey: ["UNIT_PMS_OVERRIDES_UPDATE", unit.id],
    mutationFn: async (overrides: {
      listed: boolean;
      available_for_application: boolean;
    }) => {
      await addressApi.editUnit(unit.id, { pms_overrides: overrides });
      return overrides;
    },
    onSuccess: (overrides) => {
      if (pmsAvailability != null) {
        if (overrides.available_for_application) {
          setLinkIsActive(pmsAvailability);
          if (!pmsAvailability) {
            setUnitListed(false);
            setSyndication(CLEARED_SYNDICATION);
          }
        }
        if (overrides.listed) {
          setUnitListed(pmsAvailability);
          if (pmsAvailability) setLinkIsActive(true);
        }
      }
      invalidatePropertyDetail();
    },
    onError: (error) => {
      setOverrideEdits({});
      toast.error(`Failed to update settings: ${getErrorMessage(error)}`);
    }
  });

  const handleAllowApplyingSyncChange = (synced: boolean) =>
    setOverrideEdits((prev) => ({
      ...prev,
      available_for_application: synced
    }));

  const handleListedSyncChange = (synced: boolean) =>
    setOverrideEdits((prev) => ({ ...prev, listed: synced }));

  useEffect(() => {
    const serverListed = unit.pms_overrides?.listed ?? true;
    const serverAllowApplying =
      unit.pms_overrides?.available_for_application ?? true;
    if (
      listedSynced === serverListed &&
      allowApplyingSynced === serverAllowApplying
    )
      return;

    const timeoutId = setTimeout(
      () =>
        updatePmsOverrides({
          listed: listedSynced,
          available_for_application: allowApplyingSynced
        }),
      AUTOSAVE_DEBOUNCE_MS
    );
    return () => clearTimeout(timeoutId);
  }, [listedSynced, allowApplyingSynced]);

  useEffect(() => {
    if (isPending) return;

    const dirty =
      linkIsActive !== committedRef.current.linkIsActive ||
      unitListed !== committedRef.current.unitListed ||
      !syndicationEqual(syndication, committedRef.current.syndication);

    if (!dirty) return;

    const timeoutId = setTimeout(updateUnitSettings, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [linkIsActive, unitListed, syndication, isPending]);

  useEffect(() => {
    if (isPending) return;

    const serverActive = Boolean(unit.magic_link?.active);
    const serverListed = Boolean(unit.listed);
    const serverSyndication = readSyndicationState(
      unit,
      defaultApartmentsComVariant
    );

    const serverMatchesCommitted =
      serverActive === committedRef.current.linkIsActive &&
      serverListed === committedRef.current.unitListed &&
      syndicationEqual(serverSyndication, committedRef.current.syndication);

    if (
      awaitingRefetchRef.current &&
      handleAwaitingRefetch(serverMatchesCommitted) === "done"
    )
      return;

    if (serverMatchesCommitted) return;

    const localMatchesCommitted =
      linkIsActive === committedRef.current.linkIsActive &&
      unitListed === committedRef.current.unitListed &&
      syndicationEqual(syndication, committedRef.current.syndication);

    if (!localMatchesCommitted) return;

    setLinkIsActive(serverActive);
    setUnitListed(serverListed);
    setSyndication(serverSyndication);
    committedRef.current = {
      linkIsActive: serverActive,
      unitListed: serverListed,
      syndication: serverSyndication
    };
  }, [
    unit.magic_link?.active,
    unit.listed,
    unit.feed_zillow_enabled,
    unit.feed_zillow_spotlight_enabled,
    unit.feed_apartments_com_enabled,
    unit.feed_apartments_com_variant,
    unit.feed_zumper_enabled,
    unit.feed_dwellsy_enabled,
    isPending,
    handleAwaitingRefetch,
    defaultApartmentsComVariant
  ]);

  const subjectLabel =
    propertyKind === PropertyKind.SingleFamily
      ? dictionary.property
      : dictionary.unit;
  const listToggleLabel = `List ${subjectLabel} on`;
  const allowApplyingLabel =
    propertyKind === null
      ? "Allow applying"
      : `Allow applying for ${subjectLabel}`;

  const toggleZillow = () => setSyndication(withZillowToggled);
  const toggleZumper = () => setSyndication(withZumperToggled);
  const toggleDwellsy = () => setSyndication(withDwellsyToggled);

  const toggleApartmentsCom = () =>
    setSyndication((prev) =>
      withApartmentsComToggled(prev, defaultApartmentsComVariant)
    );
  const pickApartmentsVariant = (variant: ApartmentsComVariant) =>
    setSyndication((prev) => withApartmentsVariant(prev, variant));

  return (
    <Card>
      <h3 className="text-base font-bold py-6 px-8 border-b border-alpha-40">
        Listing
      </h3>
      <div className="space-y-6 py-6 px-8">
        {pmsManaged ? (
          <div className="relative">
            <div className="flex items-center gap-4">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="w-fit shrink-0">
                    <Switch
                      label={allowApplyingLabel}
                      checked={linkIsActive}
                      disabled={
                        allowApplyingSynced || allowApplyingToggleLocked
                      }
                      className="text-base font-medium text-content-1000"
                      onChange={() => {
                        if (linkIsActive) disableAllowApplying();
                        else enableAllowApplying();
                      }}
                    />
                  </div>
                </TooltipTrigger>
                <Show if={Boolean(allowApplyingTooltip)}>
                  <TooltipContent className="max-w-[370px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-content-1000-inverted">
                    <p>{allowApplyingTooltip}</p>
                  </TooltipContent>
                </Show>
              </Tooltip>
              <RowConnector />
              <PmsSyncOption
                value={allowApplyingSynced}
                onChange={handleAllowApplyingSyncChange}
                name="available_for_application"
                order="manual-first"
                className="!mt-0"
              />
              <div className="ml-auto shrink-0">
                <PMSValue value={booleanLabel(pmsAllowApplyingValue)} />
              </div>
            </div>

            <span
              aria-hidden
              className="absolute border-l border-content-100 left-[22px] top-6 h-7"
            />
            <span
              aria-hidden
              className="absolute border-t border-content-100 left-[22px] top-[52px] w-[34px]"
            />

            <div className="ml-14 pt-4">
              <div className="flex items-center gap-4">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="w-fit shrink-0">
                      <Switch
                        label={listToggleLabel}
                        checked={unitListed}
                        disabled={listedSynced || listedToggleLocked}
                        className="text-base font-medium text-content-1000"
                        onChange={() => {
                          if (unitListed) disableShowOnPortfolio();
                          else enableShowOnPortfolio();
                        }}
                      />
                    </div>
                  </TooltipTrigger>
                  <Show if={Boolean(listedTooltip)}>
                    <TooltipContent className="max-w-[370px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-content-1000-inverted">
                      <p>{listedTooltip}</p>
                    </TooltipContent>
                  </Show>
                </Tooltip>
                <RowConnector />
                <PmsSyncOption
                  value={listedSynced}
                  onChange={handleListedSyncChange}
                  name="listed"
                  order="manual-first"
                  className="!mt-0"
                />
                <div className="ml-auto shrink-0">
                  <PMSValue value={booleanLabel(pmsListedValue)} />
                </div>
              </div>
              <SyndicationSourcesPanel
                unitListed={unitListed}
                hasListing={Boolean(unit.has_listing)}
                syndication={syndication}
                aiLeasingAgentVisible={aiLeasingAgentVisible}
                aiLeasingAgentEnabled={aiLeasingAgentEnabled}
                zillowEnabled={
                  Boolean(partner?.zillow_connected) &&
                  partnerType.isSyndication
                }
                apartmentsComEnabled={Boolean(
                  partner?.apartments_com_connected
                )}
                zumperEnabled={Boolean(partner?.zumper_connected)}
                dwellsyEnabled={Boolean(partner?.dwellsy_connected)}
                onToggleZillow={toggleZillow}
                onToggleApartmentsCom={toggleApartmentsCom}
                onPickApartmentsVariant={pickApartmentsVariant}
                onToggleZumper={toggleZumper}
                onToggleDwellsy={toggleDwellsy}
              />
            </div>
          </div>
        ) : (
          <div className="relative">
            <Switch
              label={allowApplyingLabel}
              checked={linkIsActive}
              className="text-base font-medium text-content-1000"
              onChange={() => {
                if (linkIsActive) disableAllowApplying();
                else enableAllowApplying();
              }}
            />

            <span
              aria-hidden
              className="absolute border-l border-content-100 left-[22px] top-6 h-7"
            />
            <span
              aria-hidden
              className="absolute border-t border-content-100 left-[22px] top-[52px] w-[34px]"
            />

            <div className="ml-14 pt-4">
              <Switch
                label={listToggleLabel}
                checked={unitListed}
                className="text-base font-medium text-content-1000"
                onChange={() => {
                  if (unitListed) disableShowOnPortfolio();
                  else enableShowOnPortfolio();
                }}
              />
              <SyndicationSourcesPanel
                unitListed={unitListed}
                hasListing={Boolean(unit.has_listing)}
                syndication={syndication}
                aiLeasingAgentVisible={aiLeasingAgentVisible}
                aiLeasingAgentEnabled={aiLeasingAgentEnabled}
                zillowEnabled={
                  Boolean(partner?.zillow_connected) &&
                  partnerType.isSyndication
                }
                apartmentsComEnabled={Boolean(
                  partner?.apartments_com_connected
                )}
                zumperEnabled={Boolean(partner?.zumper_connected)}
                dwellsyEnabled={Boolean(partner?.dwellsy_connected)}
                onToggleZillow={toggleZillow}
                onToggleApartmentsCom={toggleApartmentsCom}
                onPickApartmentsVariant={pickApartmentsVariant}
                onToggleZumper={toggleZumper}
                onToggleDwellsy={toggleDwellsy}
              />
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

interface SyndicationSourcesPanelProps {
  unitListed: boolean;
  hasListing: boolean;
  syndication: SyndicationState;
  aiLeasingAgentVisible: boolean;
  aiLeasingAgentEnabled: boolean;
  zillowEnabled: boolean;
  apartmentsComEnabled: boolean;
  zumperEnabled: boolean;
  dwellsyEnabled: boolean;
  onToggleZillow: () => void;
  onToggleApartmentsCom: () => void;
  onPickApartmentsVariant: (variant: ApartmentsComVariant) => void;
  onToggleZumper: () => void;
  onToggleDwellsy: () => void;
}

const SyndicationSourcesPanel = ({
  unitListed,
  hasListing,
  syndication,
  aiLeasingAgentVisible,
  aiLeasingAgentEnabled,
  zillowEnabled,
  apartmentsComEnabled,
  zumperEnabled,
  dwellsyEnabled,
  onToggleZillow,
  onToggleApartmentsCom,
  onPickApartmentsVariant,
  onToggleZumper,
  onToggleDwellsy
}: SyndicationSourcesPanelProps) => {
  const sectionDisabled = !unitListed || !hasListing;

  const lastTopLevel = computeLastTopLevel({
    aiLeasingAgentVisible,
    zillowEnabled,
    apartmentsComEnabled,
    zumperEnabled,
    dwellsyEnabled
  });

  return (
    <div className="mt-2 ml-6 relative pl-5">
      <div className="space-y-2">
        <TreeRow>
          <SourceCheckbox
            label="Portfolio page"
            icon={<BoomBrandMark />}
            checked={unitListed}
            disabled
            onChange={() => undefined}
          />
        </TreeRow>
        <TreeRow isLast={lastTopLevel === "listings"}>
          <SourceCheckbox
            label="Listings page"
            icon={<BoomBrandMark />}
            checked={unitListed}
            disabled
            onChange={() => undefined}
          />
        </TreeRow>
        <Show if={aiLeasingAgentVisible}>
          <TreeRow isLast={lastTopLevel === "aiLeasingAgent"}>
            <SourceCheckbox
              label="AI leasing agent"
              icon={<IconAILeasingAgent bgSize={20} />}
              checked={unitListed && aiLeasingAgentEnabled}
              disabled
              onChange={() => undefined}
            />
          </TreeRow>
        </Show>
        <Show if={zillowEnabled}>
          <TreeRow isLast={lastTopLevel === "zillow"}>
            <SourceCheckbox
              label="Zillow"
              icon={<SourceLogo id={IntegrationId.ZILLOW} />}
              checked={unitListed && syndication.zillow}
              disabled={sectionDisabled}
              onChange={onToggleZillow}
            />
            <div className="mt-2 ml-3 relative pl-5">
              <TreeRow isLast>
                <SourceCheckbox
                  label="Spotlight"
                  badge={<ComingSoonBadge />}
                  checked={false}
                  disabled
                  onChange={() => undefined}
                />
              </TreeRow>
            </div>
          </TreeRow>
        </Show>
        <Show if={apartmentsComEnabled}>
          <TreeRow isLast={lastTopLevel === "apartments"}>
            <SourceCheckbox
              label="Apartments.com"
              icon={<SourceLogo id={IntegrationId.APARTMENTS_COM} />}
              checked={unitListed && syndication.apartmentsCom}
              disabled={sectionDisabled}
              onChange={onToggleApartmentsCom}
            />
            <div className="mt-2 ml-3 relative pl-5">
              <div className="space-y-2">
                <TreeRow>
                  <SourceCheckbox
                    label="Single Family"
                    checked={
                      unitListed &&
                      syndication.apartmentsCom &&
                      syndication.apartmentsComVariant === "single_family"
                    }
                    disabled={sectionDisabled || !syndication.apartmentsCom}
                    onChange={() => onPickApartmentsVariant("single_family")}
                  />
                </TreeRow>
                <TreeRow isLast>
                  <SourceCheckbox
                    label="Multi Family"
                    checked={
                      unitListed &&
                      syndication.apartmentsCom &&
                      syndication.apartmentsComVariant === "multi_family"
                    }
                    disabled={sectionDisabled || !syndication.apartmentsCom}
                    onChange={() => onPickApartmentsVariant("multi_family")}
                  />
                </TreeRow>
              </div>
            </div>
          </TreeRow>
        </Show>
        <Show if={zumperEnabled}>
          <TreeRow isLast={lastTopLevel === "zumper"}>
            <SourceCheckbox
              label="Zumper"
              icon={<SourceLogo id={IntegrationId.ZUMPER} />}
              checked={unitListed && syndication.zumper}
              disabled={sectionDisabled}
              onChange={onToggleZumper}
            />
          </TreeRow>
        </Show>
        <Show if={dwellsyEnabled}>
          <TreeRow isLast={lastTopLevel === "dwellsy"}>
            <SourceCheckbox
              label="Dwellsy"
              icon={<SourceLogo id={IntegrationId.DWELLSY} />}
              checked={unitListed && syndication.dwellsy}
              disabled={sectionDisabled}
              onChange={onToggleDwellsy}
            />
          </TreeRow>
        </Show>
      </div>
    </div>
  );
};
